import clsx from 'clsx';
import { useTranslate } from 'core/hooks/useTranslate';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Avatar, Button, FormField, Icon, TextArea } from 'ui';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';

const MessageSenderSimple = ({
  currentUser,
  isFeedbackActivity = false,
  label,
  onFeedback = () => {},
  onPublishComment = () => {},
  parentGuid,
  placeholder = 'Comment...',
  showFeedbackOption = false,
  textareaRef,
}) => {
  const [text, setText] = useState('');
  const [sendEnabled, setSendEnabled] = useState(false);
  const isSending = useRef(false);
  const { t } = useTranslate();

  function onChange(e) {
    setText(e.currentTarget.value);
    setSendEnabled(e.currentTarget.value.length > 1);
  }

  function onSend() {
    if (text.trim() !== '' && !isSending.current) {
      isSending.current = true;
      onPublishComment(parentGuid, text);
      setText('');
      setSendEnabled(false);

      setTimeout(() => {
        isSending.current = false;
      }, 1000);
    }
  }

  // `aria-disabled` en vez de `disabled`: al enviar se deshabilita y, con `disabled`, el foco
  // saldría del botón; por eso el clic se ignora aquí.
  function onClickSend() {
    if (sendEnabled) {
      onSend();
    }
  }

  const handleKeypress = (e) => {
    if (e.keyCode === 13 && (e.altKey || e.metaKey)) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className={clsx(`message-sender-simple`)}>
      <div className={`message-sender-simple__main`}>
        <div className="message-sender-simple__avatar">
          <Avatar image={currentUser ? currentUser.avatar : null} />
        </div>
        <div className="message-sender-simple__textarea">
          <FormField isLabelHidden label={label ?? t('common:Comments')}>
            <TextArea onKeyDown={handleKeypress} onChange={onChange} placeholder={placeholder} ref={textareaRef} value={text} />
          </FormField>
          <button
            aria-disabled={!sendEnabled}
            aria-label={t('buttons:Send comment')}
            className={clsx('message-sender-simple__send', { 'message-sender-simple__send--disabled': !sendEnabled })}
            type="button"
            onClick={onClickSend}
          >
            <Icon name="send" size="lg" />
          </button>
        </div>
        {showFeedbackOption && !isFeedbackActivity && (
          <Tooltip title={t('assigments:Add feedback')} placement={'top'} hasArrow>
            <div className="message-sender-simple__feedback-option">
              <Button ariaLabel={t('assigments:Add feedback')} size="sm" leftIcon="flag" iconSize="md" isIconOnly={true} onClick={onFeedback} />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

MessageSenderSimple.propTypes = {
  currentUser: PropTypes.object,
  isFeedbackActivity: PropTypes.bool,
  label: PropTypes.string,
  onFeedback: PropTypes.func,
  onPublishComment: PropTypes.func,
  parentGuid: PropTypes.string,
  placeholder: PropTypes.string,
  showFeedbackOption: PropTypes.bool,
  textareaRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default MessageSenderSimple;
